import { createSlice } from "@reduxjs/toolkit";
import {
  organisationDashboardActivityAction,
  organisationDashboardCategoryAction,
  organisationDashboardTrendingTagsAction,
  organisationDashboardUserStatsAction,
  organisationDashboardVideoAction,
  organisationDashboardMostSharedVideosAction,
  organisationDashboardSharedVideoPerSchoolAction,
  organisationDashboardRecentClassesAction,
  organisationDashboardRecentCollectionsAction,
  organisationDashboardRecentActiveStaffAction,
  organisationCreateMemberAction,
  organisationDeleteMemberAction,
  organisationMemberDetailsAction,
  organisationMembersListAction,
  organisationUpdateMemberAction,
  resetOrganisationAction,
  organisationListAction,
  organisationDashboardStaffStatsAction,
  organisationDashboardCategoryPerSchoolAction,
  organisationDashboardUsageHoursAction,
} from "../Action/OrganisationAction";

const initialState = {
  dashboard: {},
  staff: [],
  memberOrganisations: [],
  organisationsList: [],
  members: [],
  memberDetails: {},
  organisationDashboardUserStats: [],
  organisationDashboardUserStatsStatus: "idle",
  organisationDashboardCategoryView: [],
  organisationDashboardCategoryViewStatus: "idle",
  organisationDashboardVideoView: [],
  organisationDashboardVideoViewStatus: "idle",
  organisationDashboardStaffSharedVideos: [],
  organisationDashboardStaffSharedVideosStatus: "idle",
  organisationDashboardSharedVideoPerSchool: [],
  organisationDashboardSharedVideoPerSchoolStatus: "idle",
  organisationDashboardActivity: [],
  organisationDashboardActivityStatus: "idle",
  organisationDashboardTrendingTags: [],
  organisationDashboardTrendingTagsStatus: "idle",
  organisationDashboardRecentClasses: { classes: [], totalClasses: 0 },
  organisationDashboardRecentClassesStatus: "idle",
  organisationDashboardRecentCollections: { collections: [], totalCollections: 0 },
  organisationDashboardRecentCollectionsStatus: "idle",
  organisationDashboardRecentActiveStaff: { staff: [], totalStaff: 0 },
  organisationDashboardRecentActiveStaffStatus: "idle",
  organisationDashboardStaffStats: [],
  organisationDashboardStaffStatsStatus: "idle",
  organisationDashboardCategoryPerSchool: [],
  organisationDashboardCategoryPerSchoolStatus: "idle",
  organisationDashboardUsageHours: [],
  organisationDashboardUsageHoursStatus: "idle",
  organisationDashboardError: null,
  membersMeta: {
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  },
  lastMemberMutation: null,
};

const organisationSlice = createSlice({
  name: "organisation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetOrganisationAction, () => initialState)
      .addCase(organisationDashboardUserStatsAction.pending, (state) => {
        state.organisationDashboardUserStatsStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardUserStatsAction.fulfilled, (state, action) => {
        state.organisationDashboardUserStatsStatus = "succeeded";
        state.organisationDashboardUserStats = action?.payload?.data || [];
      })
      .addCase(organisationDashboardUserStatsAction.rejected, (state, action) => {
        state.organisationDashboardUserStatsStatus = "failed";
        state.organisationDashboardUserStats = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardCategoryAction.pending, (state) => {
        state.organisationDashboardCategoryViewStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardCategoryAction.fulfilled, (state, action) => {
        state.organisationDashboardCategoryViewStatus = "succeeded";
        state.organisationDashboardCategoryView = action?.payload?.data || [];
      })
      .addCase(organisationDashboardCategoryAction.rejected, (state, action) => {
        state.organisationDashboardCategoryViewStatus = "failed";
        state.organisationDashboardCategoryView = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardVideoAction.pending, (state) => {
        state.organisationDashboardVideoViewStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardVideoAction.fulfilled, (state, action) => {
        state.organisationDashboardVideoViewStatus = "succeeded";
        state.organisationDashboardVideoView = action?.payload?.data || [];
      })
      .addCase(organisationDashboardVideoAction.rejected, (state, action) => {
        state.organisationDashboardVideoViewStatus = "failed";
        state.organisationDashboardVideoView = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardMostSharedVideosAction.pending, (state) => {
        state.organisationDashboardStaffSharedVideosStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardMostSharedVideosAction.fulfilled, (state, action) => {
        state.organisationDashboardStaffSharedVideosStatus = "succeeded";
        state.organisationDashboardStaffSharedVideos = action?.payload?.data || [];
      })
      .addCase(organisationDashboardMostSharedVideosAction.rejected, (state, action) => {
        state.organisationDashboardStaffSharedVideosStatus = "failed";
        state.organisationDashboardStaffSharedVideos = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardSharedVideoPerSchoolAction.pending, (state) => {
        state.organisationDashboardSharedVideoPerSchoolStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardSharedVideoPerSchoolAction.fulfilled, (state, action) => {
        state.organisationDashboardSharedVideoPerSchoolStatus = "succeeded";
        state.organisationDashboardSharedVideoPerSchool = action?.payload?.data || [];
      })
      .addCase(organisationDashboardSharedVideoPerSchoolAction.rejected, (state, action) => {
        state.organisationDashboardSharedVideoPerSchoolStatus = "failed";
        state.organisationDashboardSharedVideoPerSchool = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardActivityAction.pending, (state) => {
        state.organisationDashboardActivityStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardActivityAction.fulfilled, (state, action) => {
        state.organisationDashboardActivityStatus = "succeeded";
        state.organisationDashboardActivity = action?.payload?.data || [];
      })
      .addCase(organisationDashboardActivityAction.rejected, (state, action) => {
        state.organisationDashboardActivityStatus = "failed";
        state.organisationDashboardActivity = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardTrendingTagsAction.pending, (state) => {
        state.organisationDashboardTrendingTagsStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(
        organisationDashboardTrendingTagsAction.fulfilled,
        (state, action) => {
          state.organisationDashboardTrendingTagsStatus = "succeeded";
          state.organisationDashboardTrendingTags = action?.payload?.data || [];
        }
      )
      .addCase(
        organisationDashboardTrendingTagsAction.rejected,
        (state, action) => {
          state.organisationDashboardTrendingTagsStatus = "failed";
          state.organisationDashboardTrendingTags = [];
          state.organisationDashboardError = action?.payload || action?.error || null;
        }
      )
      .addCase(organisationDashboardRecentClassesAction.pending, (state) => {
        state.organisationDashboardRecentClassesStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardRecentClassesAction.fulfilled, (state, action) => {
        state.organisationDashboardRecentClassesStatus = "succeeded";
        state.organisationDashboardRecentClasses = action?.payload?.data || [];
      })
      .addCase(organisationDashboardRecentClassesAction.rejected, (state, action) => {
        state.organisationDashboardRecentClasses = { classes: [], totalClasses: 0 };
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardRecentCollectionsAction.pending, (state) => {
        state.organisationDashboardRecentCollectionsStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardRecentCollectionsAction.fulfilled, (state, action) => {
        state.organisationDashboardRecentCollectionsStatus = "succeeded";
        state.organisationDashboardRecentCollections = action?.payload?.data || { collections: [], totalCollections: 0 };
      })
      .addCase(organisationDashboardRecentCollectionsAction.rejected, (state, action) => {
        state.organisationDashboardRecentCollectionsStatus = "failed";
        state.organisationDashboardRecentCollections = { collections: [], totalCollections: 0 };
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardRecentActiveStaffAction.pending, (state) => {
        state.organisationDashboardRecentActiveStaffStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardRecentActiveStaffAction.fulfilled, (state, action) => {
        state.organisationDashboardRecentActiveStaffStatus = "succeeded";
        const payloadData = action?.payload?.data;
        if (Array.isArray(payloadData)) {
          state.organisationDashboardRecentActiveStaff = { staff: payloadData, totalStaff: payloadData.length };
        } else {
          state.organisationDashboardRecentActiveStaff = payloadData || { staff: [], totalStaff: 0 };
        }
      })
      .addCase(organisationDashboardRecentActiveStaffAction.rejected, (state, action) => {
        state.organisationDashboardRecentActiveStaffStatus = "failed";
        state.organisationDashboardRecentActiveStaff = { staff: [], totalStaff: 0 };
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationMembersListAction.fulfilled, (state, action) => {
        const payload = action?.payload?.data || action?.payload || {};
        const members =
          payload?.members ||
          payload?.memberList ||
          payload?.rows ||
          payload?.data ||
          [];

        state.members = Array.isArray(members) ? members : [];
        state.membersMeta = {
          total: payload?.total || 0,
          page: payload?.page || 0,
          limit: payload?.limit || 10,
          totalPages:
            payload?.totalPages ||
            Math.ceil((payload?.total || 0) / (payload?.limit || 10)),
        };
      })
      .addCase(organisationMemberDetailsAction.fulfilled, (state, action) => {
        state.memberDetails = action?.payload?.data || action?.payload || {};
      })
      .addCase(organisationCreateMemberAction.fulfilled, (state, action) => {
        state.lastMemberMutation = action?.payload || null;
      })
      .addCase(organisationUpdateMemberAction.fulfilled, (state, action) => {
        state.lastMemberMutation = action?.payload || null;
      })
      .addCase(organisationDeleteMemberAction.fulfilled, (state, action) => {
        state.lastMemberMutation = action?.payload || null;
      })
      .addCase(organisationListAction.fulfilled, (state, action) => {
        const payload = action?.payload?.data || action?.payload || {};
        const organisations =
          payload?.organisations ||
          payload?.organisationsList ||
          payload?.rows ||
          payload?.data ||
          payload?.memberList ||
          [];
        state.organisationsList = Array.isArray(organisations) ? organisations : [];
      })
      .addCase(organisationDashboardStaffStatsAction.pending, (state) => {
        state.organisationDashboardStaffStatsStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardStaffStatsAction.fulfilled, (state, action) => {
        state.organisationDashboardStaffStatsStatus = "succeeded";
        state.organisationDashboardStaffStats = action?.payload?.data || [];
      })
      .addCase(organisationDashboardStaffStatsAction.rejected, (state, action) => {
        state.organisationDashboardStaffStatsStatus = "failed";
        state.organisationDashboardStaffStats = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardCategoryPerSchoolAction.pending, (state) => {
        state.organisationDashboardCategoryPerSchoolStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardCategoryPerSchoolAction.fulfilled, (state, action) => {
        state.organisationDashboardCategoryPerSchoolStatus = "succeeded";
        state.organisationDashboardCategoryPerSchool = action?.payload?.data || [];
      })
      .addCase(organisationDashboardCategoryPerSchoolAction.rejected, (state, action) => {
        state.organisationDashboardCategoryPerSchoolStatus = "failed";
        state.organisationDashboardCategoryPerSchool = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      })
      .addCase(organisationDashboardUsageHoursAction.pending, (state) => {
        state.organisationDashboardUsageHoursStatus = "loading";
        state.organisationDashboardError = null;
      })
      .addCase(organisationDashboardUsageHoursAction.fulfilled, (state, action) => {
        state.organisationDashboardUsageHoursStatus = "succeeded";
        state.organisationDashboardUsageHours = action?.payload?.data || [];
      })
      .addCase(organisationDashboardUsageHoursAction.rejected, (state, action) => {
        state.organisationDashboardUsageHoursStatus = "failed";
        state.organisationDashboardUsageHours = [];
        state.organisationDashboardError = action?.payload || action?.error || null;
      });
  },
});

export default organisationSlice.reducer;
